import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  CardMedia,
  TextField,
  Grid,
  Paper,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CheckIcon from '@mui/icons-material/Check';
import GenerationStatusCard from '../components/GenerationStatusCard';
import VideoPlayerCard from '../components/VideoPlayerCard';
import VideoConfirmationPage from '../VideoConfirmationPage';
import ErrorState from '../components/ErrorState';
import { useMessage } from '../../../context/MessageContext';
import {
  generateVideo,
  getVideoStatus,
  captureLead,
} from '../../../apis/apiList/publicAiStudioApi';

const messages = [
  'Preparing video generation...',
  'Generating AI voice...',
  'Rendering avatar animation...',
  'Applying avatar look...',
  'Finalizing video...',
];

const GenerateVideoStep = ({
  selectedAvatar,
  selectedLook,
  selectedLookData,
  generatedLook,
  script,
  onReset,
  onProcessActive,
  onShowThankYou,
}) => {
  const { showMessage } = useMessage();
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationId, setGenerationId] = useState('video_61f8bc18-990c-46e0-9308-4bfb925c846f');
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [currentMessage, setCurrentMessage] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadStatus, setLeadStatus] = useState(null);
  const [leadError, setLeadError] = useState(null);
  const [leadFirstName, setLeadFirstName] = useState('');
  const [showThankYouOverlay, setShowThankYouOverlay] = useState(false);

  const pollingIntervalRef = React.useRef(null);

  // const isDevPreviewEnabled = true;
  const isDevPreviewEnabled = process.env.NODE_ENV !== 'production';

  const clearPreviewIntervals = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handlePreviewGenerating = () => {
    clearPreviewIntervals();
    setGenerating(true);
    setGenerationStatus('processing');
    setError(null);
    setVideoUrl(null);
    setCurrentMessage(messages[0]);

    let messageIndex = 0;
    pollingIntervalRef.current = setInterval(() => {
      messageIndex += 1;
      if (messageIndex < messages.length) {
        setCurrentMessage(messages[messageIndex]);
      } else {
        clearPreviewIntervals();
      }
    }, 2000);
  };

  const handlePreviewCompleted = () => {
    clearPreviewIntervals();
    setGenerating(false);
    setGenerationStatus('completed');
    setError(null);
    setCurrentMessage('');
    setVideoUrl('https://www.w3schools.com/html/mov_bbb.mp4');
  };

  const handleResetPreview = () => {
    clearPreviewIntervals();
    setGenerating(false);
    setGenerationStatus(null);
    setVideoUrl(null);
    setError(null);
    setCurrentMessage('');
    setGenerationId(null);
  };

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleGenerateVideo = async () => {
    try {
      setGenerating(true);
      setError(null);
      setGenerationStatus('queued');

      // Determine which look to use
      let lookToUse = null;
      if (selectedLook === 'existing' && selectedLookData) {
        lookToUse = { id: selectedLookData.generationId };
      } else if (selectedLook === 'generated' && generatedLook) {
        lookToUse = generatedLook;
      }

      const response = await generateVideo({
        type: 'video',
        avatarId: selectedAvatar.groupId || selectedAvatar._id,
        avatarHeygenId: selectedAvatar.heygenAvatarId || selectedAvatar.avatarId || selectedAvatar.cartoonAvatarId,
        title: `${selectedAvatar.groupName} Video ${new Date().toISOString()}`,
        aspectRatio: '16:9',
        removeBackground: false,
        outputFormat: 'mp4',
        script: script,
        voiceSettings: {
          speed: 1,
          pitch: 0,
          volume: 1,
          engine_settings: {
            engine_type: 'elevenlabs',
            similarity_boost: 0.5,
            stability: 0.5,
            style: 0.5,
            use_speaker_boost: true,
          },
        },
        engine: { type: 'avatar_iv' },
        resolution: '1080p',
        lookId: lookToUse?.id,
        voiceId: selectedAvatar.defaultHeygenVoiceId || selectedAvatar.elevenLabsVoiceId,
      });

      const genId = response.generationId || response.videoId;
      setGenerationId(genId);
      showMessage('Video generation started successfully.', 'success');
      startPolling(genId);
    } catch (err) {
      console.error('Failed to generate video:', err);
      const message = err.response?.data?.message || 'Failed to generate video';
      setError(message);
      showMessage(message, 'error');
      setGenerating(false);
      setGenerationStatus(null);
    }
  };

  const startPolling = (genId) => {
    setGenerationStatus('processing');
    
    const messages = [
      'Preparing video generation...',
      'Generating AI voice...',
      'Rendering avatar animation...',
      'Applying avatar look...',
      'Finalizing video...',
    ];
    let messageIndex = 0;
    setCurrentMessage(messages[messageIndex]);

    const messageInterval = setInterval(() => {
      messageIndex += 1;
      if (messageIndex < messages.length) {
        setCurrentMessage(messages[messageIndex]);
      } else {
        clearInterval(messageInterval);
      }
    }, 4000);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await getVideoStatus(genId);
        const status = statusResponse.status;

        if (status === 'completed') {
          clearInterval(pollingIntervalRef.current);
          clearInterval(messageInterval);
          setGenerationStatus('completed');
          setGenerating(false);
          setVideoUrl(statusResponse.videoUrl);
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollingIntervalRef.current);
          clearInterval(messageInterval);
          setGenerationStatus('failed');
          setGenerating(false);
          setError('Video generation failed. Please try again.');
        }
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(pollingIntervalRef.current);
        clearInterval(messageInterval);
        setGenerationStatus('failed');
        setGenerating(false);
        setError('Failed to check video generation status');
      }
    }, 5000);
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    setLeadError(null);
    setLeadStatus('submitting');

    const email = leadEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!leadFirstName.trim()) {
      setLeadError('Please enter your first name.');
      setLeadStatus(null);
      return;
    }

    if (!email) {
      setLeadError('Please enter your email address.');
      setLeadStatus(null);
      return;
    }

    if (!emailRegex.test(email)) {
      setLeadError('Please enter a valid email address.');
      setLeadStatus(null);
      return;
    }

    try {
      await captureLead({
        firstName: leadFirstName.trim(),
        email,
        generationId,
        message: 'Public AI Studio lead form submission',
      });

      setLeadStatus('submitted');
      setShowThankYouOverlay(true);
      showMessage('Your video delivery email has been sent successfully.', 'success');
      setLeadFirstName('');
      setLeadEmail('');
    } catch (err) {
      console.error('Lead capture failed:', err);
      const message = err.response?.data?.message || 'Failed to submit your email. Please try again.';
      showMessage(message, 'error');
      setLeadStatus('error');
    }
  };

  const handleGenerateAnother = () => {
    setGenerating(false);
    setGenerationStatus(null);
    setVideoUrl(null);
    setError(null);
    localStorage.removeItem('ai-studio-draft-script');
    onReset();
  };

  useEffect(() => {
    if (typeof onProcessActive === 'function') {
      onProcessActive(generating || Boolean(videoUrl));
    }
  }, [generating, videoUrl, onProcessActive]);

  const avatarImage =
    selectedLook === 'generated'
      ? generatedLook?.image
      : selectedLook === 'existing'
      ? selectedLookData?.imageUrl
      : selectedAvatar?.image;

  const lookLabel =
    selectedLook === 'generated'
      ? 'Custom Generated Look'
      : selectedLook === 'existing'
      ? 'Selected Generated Look'
      : 'Original Avatar';

  const lookPrompt =
    selectedLook === 'generated'
      ? generatedLook?.prompt
      : selectedLook === 'existing'
      ? selectedLookData?.prompt
      : '';

  if(showThankYouOverlay) {
    onShowThankYou();
  }

  if (videoUrl) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4}}>
        <Box sx={{ width: '100%', maxWidth: 1100, px: { xs: 2, md: 0 } }}>
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
            Your AI Avatar Is Ready 🎉
          </Typography>
          <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4, maxWidth: 760, mx: 'auto' }}>
            Imagine creating member updates, educational content, advocacy messages, and event promotions in minutes instead of weeks.
          </Typography>

          <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', mb: 2, width: { xs: '100%', md: '70%' }, mx: 'auto' }}>
            <VideoPlayerCard
              videoUrl={videoUrl}
              onGenerateAnother={handleGenerateAnother}
            />
          </Paper>

          <Paper elevation={3} sx={{ borderRadius: 4, p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', width: { xs: '100%', md: '70%' },
        mx: 'auto', }}>
            <Typography variant="h2" fontWeight={700} align="center" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }}>
              How Could AI Avatars Support Your Association?
            </Typography>

            
            <Typography variant="h6" align="start" sx={{ mt: 3, mb: 2, maxWidth: 760 }}>
              Association are exploring AI avatar for:
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Member Updates</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Certification Courses</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Event Promotion</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Educational Programs</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Advocacy Communications</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Executive Messages</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tell us where to send your video and we&apos;ll include practical ideas for using AI avatars at your association.
              </Typography>

              <Box component="form" onSubmit={handleLeadSubmit} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
                <TextField
                  label="First name"
                  type="text"
                  value={leadFirstName}
                  onChange={(e) => setLeadFirstName(e.target.value)}
                  required
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root':{ borderRadius: '10px' },
                  }}
                  error={Boolean(leadError)}
                  helperText={leadError || ''}
                />
                <TextField
                  label="Email Address"
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  fullWidth
                  required
                  sx={{
                    '& .MuiInputBase-root':{ borderRadius: '10px' },
                  }}
                  error={Boolean(leadError)}
                  helperText={leadError || ''}
                />
                <Button type="submit" variant="contained" size="large" sx={{ minWidth: 180, pt: '10.5px', pb: '10.5px', borderRadius: '10px' }}>
                  {leadStatus === 'submitting' ? 'Sending…' : 'SEND MY VIDEO'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                We respect your privacy and will never share your information.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (generating) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Generating Your Video
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This may take a few minutes. Please don't close this page.
        </Typography>

        <GenerationStatusCard
          status={generationStatus}
          type="video"
          currentMessage={currentMessage}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Ready to Generate
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review your selections and generate your AI video.
      </Typography>

      {error && !generating && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Video generation failed
        </Alert>
      )}

      <Card elevation={2} sx={{ mb: 3, p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'flex-start',
          }}
        >
          <Card sx={{ minWidth: 260, width: 260, borderRadius: 3, overflow: 'hidden' }}>
            <CardMedia
              component="img"
              height="260"
              image={`${avatarImage}`}
              alt={lookLabel}
              sx={{ objectFit: 'cover' }}
            />
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {lookLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedAvatar.groupName}
              </Typography>
              {lookPrompt && (
                <Typography variant="body2" color="text.secondary">
                  {lookPrompt}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box sx={{ flex: 1, minWidth: 320 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Script Preview
            </Typography>
            <Box
              sx={{
                backgroundColor: 'grey.50',
                p: 2,
                borderRadius: 2,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                maxHeight: 340,
                overflow: 'auto',
              }}
            >
              {script}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Character count: {script.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Estimated duration: ~{Math.ceil(script.length / 15)}s
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      <Alert severity="info" sx={{ my: 3 }}>
        Video generation typically takes 2-5 minutes. You'll be able to download your video once it's ready.
      </Alert>

      {isDevPreviewEnabled && (
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Button variant="outlined" onClick={handlePreviewGenerating}>
            Preview Generating UI
          </Button>
          <Button variant="outlined" onClick={handlePreviewCompleted}>
            Preview Completed UI
          </Button>
          <Button variant="text" onClick={handleResetPreview}>
            Reset Preview
          </Button>
        </Box>
      )}

      <Button
        variant="contained"
        size="large"
        startIcon={<PlayCircleIcon />}
        onClick={handleGenerateVideo}
        fullWidth
        sx={{
          py: 2,
          fontSize: '1.1rem',
          fontWeight: 600,
        }}
      >
        Generate Video
      </Button>
    </Box>
  );
};

export default GenerateVideoStep;
